/**
 * Реестр разделов CRM (DEV-1252, ADR-0042).
 *
 * Бэкенд отдаёт только ключи и порядок: что раздел называется «Клиенты», лежит
 * по /dashboard/clients и рисуется иконкой UsersIcon — знание фронта, и здесь
 * оно живёт в одном месте вместо трёх (сайдбар, роутер, гейт данных).
 *
 * Ключи в НИЖНЕМ регистре и совпадают со строками в app_sections.key дословно.
 * Верхний регистр не матчится намеренно — так DEV-1114 не повторится.
 */

export const SECTION_KEYS = [
  'dashboard',
  'clients',
  'deals',
  'properties',
  'properties_import',
  'offers',
  'calendar',
  'tasks',
  'agents',
  'agent_tickets',
  'company_verifications',
  'agents_admin',
  'partner_companies',
  'campaigns',
  'landings',
  'reports',
  'analytics_marketing',
  'rc_analytics',
  'rc_moderation',
  'chats',
  'calls',
  'videomeetings',
  'documents',
  'routing_rules',
  'admin_users',
  'admin_invitations',
  'admin_roles',
  'admin_sections',
  'admin_audit',
  'admin_messengers',
  'pipeline',
  'settings',
  'notifications',
  'profile',
] as const

export type SectionKey = (typeof SECTION_KEYS)[number]

/**
 * Группы сайдбара — вёрстка, а не модель доступа: бэкенд про них не знает.
 * Порядок группы определяется минимальным порядком её детей, поэтому
 * перестановка разделов на сервере переставляет и группу, без релиза фронта.
 */
export const SECTION_GROUPS = [
  'projects',
  'agents',
  'marketing',
  'analytics',
  'admin',
  'settings',
  'communications',
] as const

export type SectionGroup = (typeof SECTION_GROUPS)[number]

export interface SectionMeta {
  /** Маршрут раздела; совпадает с path в router/index.ts. */
  path: string
  /** Ключ i18n. Проверяется в ru-локали: EN прячет расхождения переводов. */
  labelKey: string
  /** Группа сайдбара; отсутствует у разделов первого уровня. */
  group?: SectionGroup
}

export const SECTION_REGISTRY: Record<SectionKey, SectionMeta> = {
  dashboard: { path: '/dashboard', labelKey: 'common.navigation.dashboard' },
  clients: { path: '/dashboard/clients', labelKey: 'common.navigation.clients' },
  deals: { path: '/dashboard/deals', labelKey: 'common.navigation.deals' },
  properties: {
    path: '/dashboard/properties',
    labelKey: 'common.navigation.projectsList',
    group: 'projects',
  },
  properties_import: {
    path: '/dashboard/properties/import-drive',
    labelKey: 'common.navigation.projectsImport',
    group: 'projects',
  },
  // DEV-2537. Расчёт предложения клиенту по политике продаж; гейт
  // properties:calculate_offers (миграция auth 115). Менеджеру по продажам из
  // группы открыт он и список проектов, импорт — нет.
  offers: {
    path: '/dashboard/offers',
    labelKey: 'common.navigation.offers',
    group: 'projects',
  },
  calendar: { path: '/dashboard/calendar', labelKey: 'common.navigation.calendar' },
  // DEV-1992. Борда задач — пункт первого уровня рядом с календарём: задачи и
  // календарь показывают одну и ту же работу менеджера с двух сторон.
  tasks: { path: '/dashboard/tasks', labelKey: 'common.navigation.tasks' },
  agents: {
    path: '/dashboard/agents',
    labelKey: 'common.navigation.agentsRegistry',
    group: 'agents',
  },
  agent_tickets: {
    path: '/dashboard/agent-tickets',
    labelKey: 'common.navigation.agentTickets',
    group: 'agents',
  },
  company_verifications: {
    path: '/dashboard/company-verifications',
    labelKey: 'common.navigation.moderation',
    group: 'agents',
  },
  agents_admin: {
    path: '/dashboard/agents-admin',
    labelKey: 'common.navigation.agentsAdmin',
    group: 'agents',
  },
  partner_companies: {
    path: '/dashboard/partner-companies',
    labelKey: 'common.navigation.partnerCompanies',
    group: 'agents',
  },
  // Кампании и лендинги — работа маркетолога, а не администрирование системы:
  // площадки заводят под рекламу, а не настраивают ими CRM.
  campaigns: {
    path: '/dashboard/marketing/campaigns',
    labelKey: 'common.navigation.campaigns',
    group: 'marketing',
  },
  landings: {
    path: '/dashboard/marketing/landings',
    labelKey: 'common.navigation.landings',
    group: 'marketing',
  },
  reports: {
    path: '/dashboard/reports',
    labelKey: 'common.navigation.reports',
    group: 'analytics',
  },
  // DEV-1860. Отчёт по площадкам и кампаниям: маркетинговый актив целиком, а не
  // работа конкретного менеджера. Право — reports:read_all.
  analytics_marketing: {
    path: '/dashboard/analytics/marketing',
    labelKey: 'common.navigation.analyticsMarketing',
    group: 'analytics',
  },
  rc_analytics: {
    path: '/dashboard/rc-analytics',
    labelKey: 'common.navigation.rcAnalytics',
    group: 'analytics',
  },
  rc_moderation: {
    path: '/dashboard/rc-analytics/moderation',
    labelKey: 'common.navigation.rcModeration',
    group: 'analytics',
  },
  // DEV-2761. Звонки и встречи (DEV-2086) — группа «Коммуникации»; «Чаты»
  // (DEV-2762) — в ней же. Не аналитика: журнал звонков — рабочий инструмент.
  chats: {
    path: '/dashboard/chats',
    labelKey: 'common.navigation.chats',
    group: 'communications',
  },
  calls: {
    path: '/dashboard/calls',
    labelKey: 'common.navigation.calls',
    group: 'communications',
  },
  videomeetings: {
    path: '/dashboard/videomeetings',
    labelKey: 'common.navigation.videomeetings',
    group: 'communications',
  },
  documents: { path: '/dashboard/documents', labelKey: 'common.navigation.documents' },
  // Маршрутизация лидов — часть работы с агентами (макет 737:2): правила
  // распределяют заявки между ними, и искать их в другом месте меню незачем.
  routing_rules: {
    path: '/dashboard/routing-rules',
    labelKey: 'common.navigation.routingRules',
    group: 'agents',
  },
  admin_users: {
    path: '/dashboard/admin/users',
    labelKey: 'common.navigation.adminUsers',
    group: 'admin',
  },
  admin_invitations: {
    path: '/dashboard/admin/invitations',
    labelKey: 'common.navigation.adminInvitations',
    group: 'admin',
  },
  admin_roles: {
    path: '/dashboard/admin/roles',
    labelKey: 'common.navigation.adminRoles',
    group: 'admin',
  },
  admin_sections: {
    path: '/dashboard/admin/sections',
    labelKey: 'common.navigation.adminSections',
    group: 'admin',
  },
  admin_audit: {
    path: '/dashboard/admin/audit',
    labelKey: 'common.navigation.adminAudit',
    group: 'admin',
  },
  // DEV-1712. Общие аккаунты мессенджеров компании: подключение и отключение
  // Telegram поддержки. Гейт раздела — messengers:manage (миграция auth 88).
  admin_messengers: {
    path: '/dashboard/admin/messengers',
    labelKey: 'common.navigation.adminMessengers',
    group: 'admin',
  },
  pipeline: {
    path: '/dashboard/pipeline',
    labelKey: 'common.navigation.pipelineConstructor',
    group: 'settings',
  },
  // Личные настройки пользователя: язык, тема, уведомления. Подпункт группы
  // «Настройки» — заголовок группы своей страницы не имеет.
  settings: {
    path: '/dashboard/settings',
    labelKey: 'common.navigation.personalSettings',
    group: 'settings',
  },
  notifications: {
    path: '/dashboard/notifications',
    labelKey: 'common.navigation.notifications',
    group: 'settings',
  },
  profile: { path: '/dashboard/profile', labelKey: 'common.navigation.profile', group: 'settings' },
}

/** Сужает строку из ответа сервера до известного ключа раздела. */
export const isKnownSectionKey = (value: string): value is SectionKey =>
  (SECTION_KEYS as readonly string[]).includes(value)

/**
 * Раздел, которому принадлежит маршрут. Берётся самый специфичный префикс:
 * без этого /dashboard/rc-analytics/moderation достался бы «Аналитике ЖК»,
 * а /dashboard — вообще любому разделу.
 */
export const sectionForPath = (path: string): SectionKey | undefined =>
  (Object.entries(SECTION_REGISTRY) as [SectionKey, SectionMeta][])
    .filter(([, meta]) =>
      meta.path === '/dashboard'
        ? path === meta.path
        : path === meta.path || path.startsWith(`${meta.path}/`),
    )
    .sort(([, a], [, b]) => b.path.length - a.path.length)
    .map(([key]) => key)[0]
